import BaseUISystem from "../BaseUISystem";
import BaseMenu from "./BaseMenu";
import { applyDefaultButtonAnimation } from "../UIHelper";
import RelativePositionComponent from "Component/Render/RelativePosition";
import Point from "Utility/Point";
import { IGameState } from "State/Game/IGameState";
import { DefaultQuestsPopupLayout } from "Layouts/Quests/DefaultQuestsPopupLayout";
import { UIActions } from "State/UI/UIActions";
import GameObject, { GameObjectEvent } from "GameObject/GameObject";
import Game from "Game";
import { ArrayHelper } from "Utility/ArrayHelper";
import QuestItem, { progressBarFillMaxWidth } from "Prototype/UI/QuestItem";
import QuestHelper from "Utility/QuestHelper";
import { PopupID } from "State/UI/PopupID";
import { EventLayoutMap } from "Constants/Events/EventLayoutMap";
import { EventStateHelper } from "State/Events/EventStateHelper";
import SpriteComponent from "Component/Render/Sprite";
import { PlayerActions } from "State/Player/PlayerActions";
import TextComponent from "Component/Render/Text";
import Renderable from "Component/Renderable";
import { isArray } from "lodash";
import { MathHelper } from "Utility/MathHelper";
import TransformComponent from "Component/Transform";
import DateHelper from "Utility/DateHelper";
import { Alpha } from "System/Animation/Clip";
import { StartAnimationClip } from "System/Animation/AnimationHelper";
import { IRewardData } from "Utility/RewardHelper";
import { AnimationClipEvent } from "System/Animation/AnimationClip";
import LevelHelper from "Utility/LevelHelper";
import { getHighestLevelCompleted } from "State/Player/PlayerLevelHelper";
import { getFarmItemThumbnailAssetName, getFarmItemThumbnailPath } from "System/Farm/FarmHelpers";
import { report } from "report";
import getActiveEvents = EventStateHelper.getActiveEvents;
import isXmasEvent = EventStateHelper.isXmasEvent;
import getEvent = EventStateHelper.getEvent;

export default class QuestsMenuHandler extends BaseMenu {
    private layout: any;
    private questItems: QuestItem[] = [];

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.QUEST_MENU, uiSystem, false);
    }

    public onCreate() {
        super.onCreate();

        const state = Game.store.getState();
        const validEvent = ArrayHelper.sortByHighestPriority(getActiveEvents(state)).find(
            event => EventLayoutMap[event.type]?.[PopupID.QUEST_MENU]
        );

        if (validEvent && EventLayoutMap[validEvent.type] && EventLayoutMap[validEvent.type][PopupID.QUEST_MENU]) {
            this.layout = new EventLayoutMap[validEvent.type][PopupID.QUEST_MENU]();
        } else {
            this.layout = new DefaultQuestsPopupLayout();
        }

        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        this.initButton(this.layout.subLayout.button, this.close);
        this.initButton(this.layout.subLayout.closeButton, this.close);

        this.layout.subLayout.quests.forEach((questObject: any, index: number) => {
            const questItem = new QuestItem({
                layout: this.layout.subLayout.quests[index],
                data: state.playerState.quests[index],
                questIndex: index,
            });

            this.questItems.push(questItem);
            this.cleanupManager.add(() => {
                questItem.destroy();
                this.questItems = ArrayHelper.remove(this.questItems, questItem);
            });
        });

        if (this.layout.eventSubLayout) {
            const eventQuest = EventStateHelper.getActiveQuest(state);
            const finalTarget = QuestHelper.getFinalQuestTarget(
                eventQuest.type,
                eventQuest.tier,
                QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
            );
            const eventQuestTargets = QuestHelper.getQuestTarget(
                eventQuest.type,
                eventQuest.tier,
                QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
            );

            const button = this.layout.eventSubLayout.collectButton;

            this.initButton(button, () =>
                this.collectEventQuestReward(eventQuest.id)
            );

            const backgroundTexture = QuestHelper.getBackgroundTileKey(
                eventQuest.type,
                eventQuest.tier,
                QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
            );

            this.layout.eventSubLayout.eventBG.getComponent(
                SpriteComponent
            ).texture = backgroundTexture;

            // Set quest title
            const questTitle = this.layout.eventSubLayout.missionText;
            const questTitleText = Game.locale.getText(QuestHelper.getTitleKey(eventQuest.type));
            questTitle.getComponent(TextComponent).text = questTitleText;

            // Set progress text
            const progressBarText = this.layout.eventSubLayout.progressText;
            const progressText = `${eventQuest.progress} / ${finalTarget}`;
            progressBarText.getComponent(TextComponent).text = progressText;

            this.updateEventData(state);

            if (isArray(eventQuestTargets)) {
                this.layout.eventSubLayout.giftTooltipText.forEach((text: any, index: number) => {
                    const currentTarget = eventQuestTargets[index];
                    const giftText = this.layout.eventSubLayout.giftTooltipText[index];

                    const giftTextComponent = giftText.getComponent(TextComponent);
                    giftTextComponent.text = String(currentTarget);

                    const parentTransform = giftText.parent.getComponent(TransformComponent);
                    const percProgress = MathHelper.normalize(
                        currentTarget,
                        0,
                        finalTarget
                    );

                    parentTransform.x = Math.min(
                        MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
                        progressBarFillMaxWidth - 15);

                    const rewards = QuestHelper.getQuestRewards(0, QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType));
                    if (!isArray(rewards[index])) {
                        report("Multi target quest with mismatching rewards", {
                            once: true,
                            sampleRate: 0.01,
                            tags: {
                                eventType: eventQuest.eventType,
                                tier: eventQuest.tier
                            },
                            fingerprint: "QuestMenuHandler:onCreate"
                        });
                        return;
                    }
                    const targetRewards = rewards[index] as IRewardData[];
                    giftText.parent.on(GameObjectEvent.SELECTED, () => {
                        this.onGiftClicked(targetRewards);
                    });
                    this.cleanupManager.add(() => {
                        giftText.parent.off(GameObjectEvent.SELECTED, () => {
                            this.onGiftClicked(targetRewards);
                        });
                    });
                });
            }

            // Set timer
            const timerTextComponent = this.layout.eventSubLayout.timeText.getComponent(TextComponent);
            timerTextComponent.text = DateHelper.timeTo(DateHelper.now, eventQuest.expiration, 'DD:hh:mm:ss');
            const timerInterval = Game.ticker.setInterval(() => {
                timerTextComponent.text = DateHelper.timeTo(DateHelper.now, eventQuest.expiration, 'DD:hh:mm:ss');
            }, 1000);
            this.cleanupManager.add(() => {
                Game.ticker.clearInterval(timerInterval);
            });

            this.setEventQuestProgress(state);
        }
    }

    private onGiftClicked(rewards: IRewardData[]) {
        const state = Game.store.getState();

        const itemReward = rewards[0];
        const coinReward = rewards[1];

        if (typeof itemReward.value === "string") {
            const itemId = itemReward.value;
            const itemSprite = this.layout.eventSubLayout.itemSprite;
            const itemSpriteComponent = itemSprite.getComponent(SpriteComponent);
            const texture = getFarmItemThumbnailAssetName(itemId);
            if (!Game.resource.existsOrQueued(texture)) {
                Game.resource.add(
                    texture,
                    getFarmItemThumbnailPath(itemId)
                );
            }
            Game.resource.wait(texture).then(() => {
                itemSpriteComponent.texture = texture;
            });
        }

        const levelCost = LevelHelper.getLevelCost(
            getHighestLevelCompleted(state.playerState)
        );

        this.layout.eventSubLayout.rewardText.getComponent(TextComponent).text = `${Math.round(coinReward.value * levelCost)}`;
        this.layout.eventSubLayout.rewardSprite.getComponent(SpriteComponent).texture = coinReward.iconCacheKey;

        const overlay = this.layout.eventSubLayout.overlay;
        const giftDetailsSprite = this.layout.eventSubLayout.giftDetailsSprite;

        this.fadeIn(overlay);
        this.fadeIn(giftDetailsSprite);

        Game.ticker.setTimeout(() => {
            this.layout.eventSubLayout.overlay.once(GameObjectEvent.SELECTED,
                () => {
                    this.fadeOut(overlay);
                    this.fadeOut(giftDetailsSprite);
                }
            );
        }, 500);
    }

    private fadeIn(object: any) {
        StartAnimationClip(
            new Alpha(object, {
                startAlpha: 0,
                endAlpha: 1,
                duration: 450,
            })
        );
    }

    private fadeOut(object: any) {
        StartAnimationClip(
            new Alpha(object, {
                startAlpha: 1,
                endAlpha: 0,
                duration: 450,
            }).once(AnimationClipEvent.DESTROY, () => {
                object.getComponent(Renderable).visible = false;
            })
        );
    }

    private setEventQuestProgress(state: IGameState) {
        if (!this.layout.eventSubLayout) return;

        const fill = this.layout.eventSubLayout.progressBarFill;
        const text = this.layout.eventSubLayout.progressText;
        const eventQuest = EventStateHelper.getActiveQuest(state);
        const target = QuestHelper.getFinalQuestTarget(
            eventQuest.type,
            eventQuest.tier,
            QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
        );

        // Set bar fill length
        if (eventQuest.progress !== 0) {
            const percProgress = MathHelper.normalize(
                eventQuest.progress,
                0,
                target
            );
            fill.getComponent(TransformComponent).width = Math.min(
                MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
                progressBarFillMaxWidth
            );
        } else {
            fill.getComponent(TransformComponent).width = 0;
        }
        let currentProgress = MathHelper.clamp(
            eventQuest.progress,
            0,
            target
        );
        // Set Progress text
        text.getComponent(
            TextComponent
        ).text = `${currentProgress} / ${target}`;
    }

    public onShow(params: unknown) {
        const state = Game.store.getState();
        this.questItems.forEach((questItem, index) => {
            questItem.updateData(state.playerState.quests[index], state);
        });
        this.updateEventData(state);
    }

    private readonly close = () => {
        if (this.blockClose) return;
        UIActions.hidePopup(PopupID.QUEST_MENU);
    };

    private initButton(button: GameObject, onClick: () => unknown) {
        const cleanup = applyDefaultButtonAnimation(button);
        button.on(GameObjectEvent.SELECTED, onClick);
        this.cleanupManager.add(() => {
            cleanup();
            button.off(GameObjectEvent.SELECTED, onClick);
        });
    }

    private readonly collectEventQuestReward = (id: string): void => {
        PlayerActions.claimQuestRewardById(id);
    };

    public async preload() {
        const state = Game.store.getState();
        state.playerState.quests.forEach(quest => {
            if (!QuestHelper.isQuestOnCooldown(quest)) {
                QuestHelper.addTileIfNeeded(quest.type, quest.tier);
            }
        });
        // If there is an active quest then preload the event quest assets
        // TODO: This should be dynamic like the layouts in some way
        const eventQuest = EventStateHelper.getActiveQuest(state);
        if (eventQuest) {
            this.resources.set('event-quest-atlas', 'resource/graphics/sprite_sheets/event_quests.json');
            if (eventQuest.type) {
                const eventPoolKey = QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType);
                QuestHelper.addTileIfNeeded(eventQuest.type, eventQuest.tier, eventPoolKey);
            }
        }

        // TODO: Make dynamic - logic from event offer `queueAssets` should be consolidated.
        if (isXmasEvent(getEvent(Game.store.getState(), PopupID.QUEST_MENU))) {
            if (!Game.resource.existsOrQueued('xmas_2021')) {
                this.resources.set('xmas_2021', 'resource/graphics/sprite_sheets/xmas_2021.json');
            }
        }

        await super.preload();
    }

    public handleStateChanged(state: IGameState): void {
        super.handleStateChanged(state);
        this.questItems.forEach(questItem => questItem.handleStateChanged(state));
        this.updateEventData(state);
        this.setEventQuestProgress(state);
    }

    private updateEventQuestCanCollect(canCollect: boolean): void {
        this.layout.eventSubLayout.progressBarFill.getComponent(SpriteComponent).texture = canCollect ? 'mission_bar_green' : 'mission_bar_blue';
        this.layout.eventSubLayout.collectButton.getComponent(Renderable).visible = canCollect;
    }

    private updateEventData(state: IGameState): void {
        if (!this.layout.eventSubLayout) return;

        const eventQuest = EventStateHelper.getActiveQuest(state);
        const canCollect = QuestHelper.canCompleteQuest(eventQuest);
        this.updateEventQuestCanCollect(canCollect);

        // Check event quest complete.
        if (eventQuest.cooldownStartedAt !== null) {
            this.layout.eventSubLayout.lockSprite.getComponent(Renderable).visible = false;
            this.layout.eventSubLayout.bigCheckSprite.getComponent(Renderable).visible = true;
            this.layout.eventSubLayout.shadowSprite.getComponent(Renderable).visible = true;
        }
    }

    public get blockClose(): boolean {
        return Boolean(this.questItems.find((e: QuestItem) => e && e.isAnimating));
    }
}
